import React, { useState, useEffect } from "react";
import { db, ref, onValue, push, remove, update, get } from "./firebase";
import { formatCourseId } from "./utils/formatCourse";

// Date formatting helper
const formatDate = (date, options = {}) => {
    if (!date) return '';
    const defaultOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

const AdminAnnouncements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [newAnnouncement, setNewAnnouncement] = useState({
        title: "",
        content: ""
    });
    const [editingId, setEditingId] = useState(null);

    // Fetch courses
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const coursesRef = ref(db, "courses");
                const snapshot = await get(coursesRef);
                if (snapshot.exists()) {
                    const coursesData = snapshot.val();
                    const coursesList = Object.keys(coursesData).map(courseId => ({
                        id: courseId,
                        name: coursesData[courseId].name || courseId
                    }));
                    setCourses(coursesList);
                }
            } catch (err) {
                console.error("Error fetching courses:", err);
                setError("Failed to load courses. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    // Fetch announcements for selected course
    useEffect(() => {
        if (!selectedCourse) {
            setLoading(false);
            return;
        }

        const announcementsRef = ref(db, `courses/${selectedCourse}/announcements`);
        const unsubscribe = onValue(announcementsRef, (snapshot) => {
            try {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const announcementsList = Object.entries(data).map(([id, announcement]) => ({
                        id,
                        ...announcement,
                        date: new Date(announcement.date)
                    })).sort((a, b) => b.date - a.date);
                    setAnnouncements(announcementsList);
                } else {
                    setAnnouncements([]);
                }
                setError(null);
            } catch (err) {
                console.error("Error fetching announcements:", err);
                setError("Failed to load announcements. Please try again.");
            } finally {
                setLoading(false);
            }
        }, (error) => {
            console.error("Error in announcements listener:", error);
            setError("Failed to load announcements. Please try again.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [selectedCourse]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim() || !selectedCourse) {
            setError("Please fill in all fields and select a course");
            return;
        }

        try {
            setLoading(true);
            const announcementRef = ref(db, `courses/${selectedCourse}/announcements`);
            const newAnnouncementData = {
                ...newAnnouncement,
                date: new Date().toISOString()
            };

            if (editingId) {
                await update(ref(db, `courses/${selectedCourse}/announcements/${editingId}`), newAnnouncementData);
                setEditingId(null);
            } else {
                await push(announcementRef, newAnnouncementData);
            }

            setNewAnnouncement({ title: "", content: "" });
            setError(null);
        } catch (err) {
            console.error("Error saving announcement:", err);
            setError(`Failed to save announcement: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (announcement) => {
        setNewAnnouncement({
            title: announcement.title,
            content: announcement.content
        });
        setEditingId(announcement.id);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this announcement?")) {
            try {
                await remove(ref(db, `courses/${selectedCourse}/announcements/${id}`));
                setError(null);
            } catch (err) {
                console.error("Error deleting announcement:", err);
                setError("Failed to delete announcement. Please try again.");
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-0">
                    Manage Announcements
                </h1>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            {/* Course selector */}
            <div className="mb-6">
                <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                <select
                    id="course"
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="w-full md:w-64 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-blue-500 min-w-[200px]"
                >
                    <option value="">Select course</option>
                    {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                            {formatCourseId(course.id)}
                        </option>
                    ))}
                </select>
            </div>

            {!selectedCourse ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
                    <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
                    <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
                </div>
            ) : (
                <>
            {/* New Announcement Form */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Create New Announcement</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            Title
                        </label>
                        <input
                            type="text"
                            id="title"
                            value={newAnnouncement.title}
                            onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                            Content
                        </label>
                        <textarea
                            id="content"
                            value={newAnnouncement.content}
                            onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                            rows="4"
                            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-blue-500"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                        {editingId ? "Update Announcement" : "Create Announcement"}
                    </button>
                </form>
            </div>

            {/* Announcements List */}
            <div className="space-y-4">
                {announcements.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">
                        No announcements yet for this course. Create your first announcement above.
                    </div>
                ) : (
                    announcements.map((announcement) => (
                        <div
                            key={announcement.id}
                            className="bg-white p-6 rounded-lg shadow-md border border-gray-200"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                                        {formatDate(announcement.date)}
                                    </span>
                                    <button
                                        onClick={() => handleEdit(announcement)}
                                        className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(announcement.id)}
                                        className="p-2 text-red-600 hover:text-red-700 transition-colors"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                        </div>
                    ))
                )}
            </div>
                </>
            )}
        </div>
    );
};

export default AdminAnnouncements; 